import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, Phone } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Breadcrumb from "@/components/site/Breadcrumb";
import EmergencyBanner from "@/components/site/EmergencyBanner";
import ContactForm from "@/components/site/ContactForm";
import { Reveal } from "@/components/site/Reveal";
import { COMPANY } from "@/data/content";
import { setSeo, setJsonLd } from "@/lib/seo";
import { renderWithLinks } from "@/lib/richText";
import api from "@/lib/api";

const SITE = "https://serrurier-lagny-sur-marne.com";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    setSeo({
      title: post.meta_title
        ? `${post.meta_title} — Serrurier Lagny sur Marne`
        : `${post.title} — Serrurier Lagny sur Marne`,
      description: post.meta_description || `☎ ${COMPANY.phoneDisplay} — ${post.excerpt}`,
      canonical: `${SITE}/blog/${post.slug}`,
    });
    setJsonLd({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: post.title,
          description: post.excerpt,
          image: `${SITE}${post.image}`,
          datePublished: post.date,
          author: { "@type": "Organization", name: COMPANY.name },
          publisher: { "@type": "Organization", name: COMPANY.name },
          mainEntityOfPage: `${SITE}/blog/${post.slug}`,
          keywords: post.keywords,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
            { "@type": "ListItem", position: 3, name: post.title, item: `${SITE}/blog/${post.slug}` },
          ],
        },
      ],
    });
    window.scrollTo(0, 0);
  }, [post]);

  if (notFound) return <Navigate to="/blog" replace />;
  if (!post)
    return (
      <div className="min-h-screen grid place-items-center bg-brand-mist text-slate-500">
        Chargement…
      </div>
    );

  return (
    <div className="App relative">
      <Navbar />
      <main>
        <article>
          <section className="bg-brand-mist pt-32 pb-14">
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
              <Breadcrumb
                items={[
                  { label: "Accueil", to: "/" },
                  { label: "Blog", to: "/blog" },
                  { label: post.title },
                ]}
              />
              <div className="flex items-center gap-4 text-xs uppercase tracking-[0.12em] text-slate-500 font-medium">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> {post.dateLabel}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> {post.readingTime}
                </span>
              </div>
              <h1 className="mt-4 font-display font-semibold text-3xl md:text-5xl tracking-tight text-brand-navy leading-[1.1]">
                {post.title}
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                {post.excerpt}
              </p>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-white">
            <div className="max-w-3xl mx-auto px-6 lg:px-12">
              <div className="aspect-[16/9] overflow-hidden rounded-2xl border border-brand-line mb-10">
                <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
              </div>
              <div className="space-y-10">
                {post.sections.map((s, i) => (
                  <Reveal key={i}>
                    <h2 className="font-display font-semibold text-2xl md:text-3xl text-brand-navy mb-4 leading-tight">
                      {s.h2}
                    </h2>
                    {s.p.map((para, j) => (
                      <p key={j} className="text-slate-700 leading-relaxed text-base md:text-lg mb-4">
                        {renderWithLinks(para)}
                      </p>
                    ))}
                  </Reveal>
                ))}
              </div>

              <Reveal>
                <div className="mt-14 rounded-2xl bg-brand-navy p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div>
                    <div className="font-display font-semibold text-2xl text-white">
                      Besoin d&apos;un serrurier maintenant ?
                    </div>
                    <p className="text-white/70 mt-1">
                      Intervention en 30 min à Lagny et alentours, devis gratuit.
                    </p>
                  </div>
                  <a
                    href={`tel:${COMPANY.phoneTel}`}
                    data-testid="blog-call-btn"
                    className="inline-flex items-center gap-3 bg-brand-gold hover:bg-brand-golddark text-brand-navy px-8 py-4 rounded-full font-semibold text-base transition-colors duration-300 shrink-0"
                  >
                    <Phone className="w-5 h-5" /> {COMPANY.phoneDisplay}
                  </a>
                </div>
              </Reveal>
            </div>
          </section>
        </article>

        <ContactForm />
      </main>
      <Footer />
      <EmergencyBanner />
    </div>
  );
}
